import { ChangeDetectionStrategy, Component, ElementRef, computed, effect, inject, input, output, viewChild } from '@angular/core';
import { MbaIcon } from '../../../assignment/atoms/mba-icon/mba-icon';
import {
  PLATFORM_LABEL,
  PLATFORM_LOGO,
  ago,
  channelLabel,
  compact,
  full,
  initials,
  percent,
  relativeDay,
} from '../../hybrid-reporting.helpers';
import { DailyPoint, METRIC_KEYS, MetricKey, ReportRow } from '../../hybrid-reporting.types';

const SPARK_W = 400;
const SPARK_H = 64;

/**
 * One post in detail. Fixed to the right edge with no scrim, so the table stays usable behind
 * it. ↑/↓ step through the table's current order, Esc closes, a click on empty page closes.
 */
@Component({
  selector: 'hr-post-drawer',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MbaIcon],
  templateUrl: './hr-post-drawer.html',
  host: {
    '(document:keydown)': 'onKey($event)',
    '(document:click)': 'onDocumentClick($event)',
  },
})
export class HrPostDrawer {
  readonly row = input.required<ReportRow>();
  readonly index = input.required<number>();
  readonly count = input.required<number>();
  readonly series = input<DailyPoint[] | null>(null);
  readonly seriesLoading = input(false);
  readonly statsLoading = input(false);
  /** Brief average engagement rate, percent. */
  readonly briefEr = input<number | null>(null);
  readonly now = input.required<Date>();

  readonly close = output<void>();
  readonly step = output<1 | -1>();

  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly closeButton = viewChild<ElementRef<HTMLButtonElement>>('closeBtn');

  protected readonly logos = PLATFORM_LOGO;
  protected readonly platformLabel = PLATFORM_LABEL;
  protected readonly sparkW = SPARK_W;
  protected readonly sparkH = SPARK_H;

  constructor() {
    // Move focus into the drawer when it opens, not on every step.
    let opened = false;
    effect(() => {
      const button = this.closeButton();
      if (button && !opened) {
        opened = true;
        button.nativeElement.focus({ preventScroll: true });
      }
    });
  }

  protected readonly name = computed(() => this.row().creator?.name ?? 'Creator');
  protected readonly firstName = computed(() => this.name().split(' ')[0]);
  protected readonly avatarInitials = computed(() => initials(this.name()));
  protected readonly platform = computed(() => channelLabel(this.row().post.socialChannel));

  protected readonly stats = computed(() => {
    const r = this.row();
    return [
      { label: 'Views', value: full(r.views) },
      { label: 'Likes', value: full(r.likes) },
      { label: 'Comments', value: full(r.comments) },
      { label: 'Shares', value: full(r.shares) },
      { label: 'Saves', value: full(r.saves) },
      { label: 'Engagement rate', value: percent(r.er) },
    ];
  });

  protected readonly followers = computed(() => {
    const f = this.row().stats?.followers;
    return f === null || f === undefined ? null : compact(f);
  });

  protected readonly posted = computed(() => relativeDay(this.row().postedAt, this.now()));
  protected readonly refreshed = computed(() => ago(this.row().stats?.refreshedAt, this.now()));

  /** Which source each stat came from, in words. */
  protected readonly sourceNote = computed(() => {
    const s = this.row().stats;
    if (!s?.source) return 'Neither Ultra Social nor Super Social has stats for this post yet.';
    const ultra = METRIC_KEYS.filter((k: MetricKey) => s.sources[k] === 'ultra');
    const sup = METRIC_KEYS.filter((k: MetricKey) => s.sources[k] === 'super');
    if (!sup.length) return 'All stats come from Ultra Social.';
    if (!ultra.length) return `All stats come from Super Social; Ultra Social had none for this post.`;
    return `${cap(ultra.join(', '))} from Ultra Social; ${sup.join(', ')} from Super Social, which filled what Ultra Social didn't have.`;
  });

  protected readonly spark = computed(() => {
    const posted = this.row().postedAt?.slice(0, 10);
    const points = (this.series() ?? []).filter((p) => !posted || p.date >= posted);
    const values = points.map((p) => p.views ?? 0);
    if (values.length < 2) return null;
    const max = Math.max(1, ...values);
    const step = SPARK_W / (values.length - 1);
    const line = values.map((v, i) => `${i ? 'L' : 'M'}${(i * step).toFixed(1)},${(SPARK_H - 4 - (v / max) * (SPARK_H - 8)).toFixed(1)}`).join('');
    return {
      line,
      area: `${line}L${SPARK_W},${SPARK_H}L0,${SPARK_H}Z`,
      caption: `${compact(values[values.length - 1])} in ${values.length} day${values.length === 1 ? '' : 's'}`,
    };
  });

  protected readonly compare = computed(() => {
    const er = this.row().er;
    const usual = this.row().stats?.usualEngagementRate ?? null;
    const brief = this.briefEr();
    const known = [er, usual, brief].filter((v): v is number => v !== null && Number.isFinite(v));
    if (er === null || !known.length) return null;
    const max = Math.max(...known, 0.1);
    const bars = [
      { label: 'This post', value: er, color: 'var(--app-accent)' },
      ...(usual !== null ? [{ label: `${this.firstName()}'s usual`, value: usual, color: 'var(--fg-1)' }] : []),
      ...(brief !== null ? [{ label: 'Brief average', value: brief, color: 'var(--border-strong)' }] : []),
    ].map((b) => ({ ...b, width: (b.value / max) * 100, text: percent(b.value) }));
    let verdict: { up: boolean; text: string } | null = null;
    if (usual !== null && usual > 0) {
      verdict =
        er >= usual
          ? { up: true, text: `${(er / usual).toFixed(1)}× ${this.firstName()}'s usual engagement` }
          : { up: false, text: `Below ${this.firstName()}'s usual engagement` };
    }
    return { bars, verdict };
  });

  protected onKey(event: KeyboardEvent): void {
    const target = event.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      this.close.emit();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (this.index() < this.count() - 1) this.step.emit(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (this.index() > 0) this.step.emit(-1);
    }
  }

  /** Clicking empty page space closes; clicks inside the drawer, on a row or on a control don't. */
  protected onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target || !target.isConnected) return;
    if (this.host.nativeElement.contains(target)) return;
    if (target.closest('tr.row, button, a, input, [role="tab"], [role="dialog"], .cdk-overlay-container')) return;
    this.close.emit();
  }
}

function cap(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}
